import React from 'react';

const C = {
  navyA: '#0a0e2b',
  navyB: '#111b5e',
  navy: '#0f1847',
  navyCard: '#1a2666',
  gold: '#D4AF37',
  blueGray: '#8a9cc5',
  lightBlue: '#c5cfe8',
  greenActive: '#16a34a',
  greenBg: '#f0fdf4',
  redAlert: '#dc2626',
  redBg: '#fef2f2',
  redBorder: '#fecaca',
  pageBg: '#f4f5f8',
  card: '#ffffff',
  border: '#edf0f5',
};

class AllLocationPlan extends React.Component {
  getInitials = (name) =>
    (name || '')
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  getFilteredLocations = () => {
    const allCompanies = this.props.availableCompanies || [];
    const selectedLocationId = this.props.user?.selectedLocationId;
    const selectedCompany = allCompanies.find(
      (c) => c.companyId === selectedLocationId
    );
    const isNDS = selectedCompany?.name === 'NuVe System';

    if (isNDS) {
      // NDS selected: show every location except NDS itself
      return allCompanies.filter((loc) => loc.name !== 'NuVe System');
    }

    // Non-NDS selected: show its linked locations + companies that link back to it
    const linkedIds = new Set(selectedCompany?.linkedCompanies || []);

    // Also find companies that link TO the selected company (reverse lookup)
    allCompanies.forEach((c) => {
      if (
        c.linkedCompanies &&
        c.linkedCompanies.includes(selectedLocationId) &&
        c.name !== 'NuVe System'
      ) {
        linkedIds.add(c.companyId);
      }
    });

    linkedIds.add(selectedLocationId); // include itself
    return allCompanies.filter(
      (loc) => loc.name !== 'NuVe System' && linkedIds.has(loc.companyId)
    );
  };

  render() {
    const locations = this.getFilteredLocations();

    return (
      <div
        style={{
          minHeight: '100vh',
          background: C.pageBg,
          fontFamily: "'SF Pro Display','Segoe UI',-apple-system,sans-serif",
        }}
      >
        <div style={{ padding: '16px 16px' }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: '#001665',
              marginBottom: 10,
              marginTop: '16px',
            }}
          >
            Linked Companies
          </div>

          {locations.length === 0 ? (
            <div
              style={{
                background: C.card,
                borderRadius: 16,
                padding: '40px 24px',
                textAlign: 'center',
                border: `1.5px dashed ${C.border}`,
                boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  margin: '0 auto 14px',
                  background: `linear-gradient(135deg, ${C.navy}, ${C.navyCard})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={C.gold}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: C.navy,
                  marginBottom: 4,
                }}
              >
                No Linked Locations
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: C.blueGray,
                  lineHeight: 1.5,
                  maxWidth: 260,
                  margin: '0 auto',
                }}
              >
                This company doesn't have any linked locations yet. Linked
                locations can be managed in Settings.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {locations.map((loc) => {
                const isActive =
                  loc.enabled &&
                  loc.status !== 'hold' &&
                  loc.status !== 'closed';
                const isUnpaid = !isActive;
                return (
                  <div
                    key={loc.companyId}
                    onClick={() => this.props.handleOpenLocation(loc)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 14px',
                      background: C.card,
                      borderRadius: 12,
                      cursor: 'pointer',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      border: isUnpaid
                        ? `1.5px solid ${C.redBorder}`
                        : `1px solid ${C.border}`,
                      transition: 'box-shadow .15s',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          flexShrink: 0,
                          background: `linear-gradient(135deg, ${C.navy}, ${C.navyCard})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: C.gold,
                          fontSize: 13,
                          fontWeight: 800,
                        }}
                      >
                        {this.getInitials(loc.name)}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: C.navy,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {loc.name}
                        </div>
                        <div style={{ fontSize: 13, color: C.blueGray }}>
                          {loc.companyCode ? `${loc.companyCode} · ` : ''}
                          {loc.planName || 'No plan'}{' '}
                          {loc.amount ? `· $${loc.amount}` : ''}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 4,
                          background: isActive ? C.greenBg : C.redBg,
                          color: isActive ? C.greenActive : C.redAlert,
                        }}
                      >
                        {isActive ? 'ACTIVE' : 'UNPAID'}
                      </span>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={C.lightBlue}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default AllLocationPlan;
